import { StandardProps } from "@/types/types";
import { FoundItLink } from "@/types/foundIt";
import { SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { useMemo, useRef, useEffect, useState } from "react";
import styles from "./FoundItLinks.module.scss";

type FoundItLinksComponentProps = Pick<
  StandardProps,
  "configProps" | "foundItData"
>;

const MIN_LINKS_TO_SHOW = 2;
const MAX_LINKS_TO_SHOW = 12;

export function FoundItLinks({
  foundItData,
  configProps,
}: FoundItLinksComponentProps) {
  const linksFoundItData = foundItData.links;

  const maxItems = (configProps?.maxItems as number) || MAX_LINKS_TO_SHOW;

  const links: FoundItLink[] = useMemo(() => {
    return linksFoundItData?.slice(0, maxItems) || [];
  }, [linksFoundItData, maxItems]);

  // --- Overflow detection logic ---
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const checkOverflow = () => {
      setIsOverflowing(wrapper.scrollWidth > Math.round(wrapper.clientWidth));
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [links.length]);

  if (!linksFoundItData || linksFoundItData.length < MIN_LINKS_TO_SHOW) {
    return null;
  }

  return (
    <div
      data-component="FoundItLinks"
      className={styles.foundItLinks}
      role="region"
      aria-labelledby="foundit-links-title"
    >
      <h2 id="foundit-links-title" className={styles.title}>
        Discover more
      </h2>
      <div className={styles.linksContainer}>
        <div
          className={
            isOverflowing
              ? `${styles.linksWrapper} ${styles.overflowing}`
              : styles.linksWrapper
          }
          ref={wrapperRef}
        >
          {links.map((link, i) => {
            const { AnchorText, DestinationUrl } = link;
            return (
              <SecondaryButton
                key={i}
                className={styles.linkButton}
                size="small"
                tag="a"
                type="button"
                href={DestinationUrl}
              >
                {AnchorText}
              </SecondaryButton>
            );
          })}
        </div>
      </div>
    </div>
  );
}
